/**
 * TISA COINS HELPER FUNCTIONS
 *
 * Pure functions to compute current state from event arrays.
 * No database queries - just array operations on JSONB data.
 */

import type {
  TISAUser,
  XPEvent,
  CoinEvent,
  QuestEvent,
  BadgeEvent,
  StudentStats,
  QuestState,
  BadgeProgress,
  BankAccountSummary,
  Quest,
} from "../types/tisa-coins";
import {
  XP_TO_LEVEL,
  LEVEL_TO_XP,
  getNextLevelPerks,
  BANK_ACCOUNTS,
  getBadgeById,
} from "../config/tisa-settings";

// ============================================================================
// XP & LEVEL CALCULATIONS
// ============================================================================

/**
 * Calculate total XP from xp_events array
 */
export const calculateTotalXP = (xp_events: XPEvent[]): number => {
  return xp_events.reduce((total, event) => total + event.amount, 0);
};

/**
 * Calculate current level and progress
 */
export const calculateLevelProgress = (total_xp: number) => {
  const currentLevel = XP_TO_LEVEL(total_xp);
  const currentLevelXP = LEVEL_TO_XP(currentLevel);
  const nextLevelXP = LEVEL_TO_XP(currentLevel + 1);
  const xpInCurrentLevel = total_xp - currentLevelXP;
  const xpNeededForNextLevel = nextLevelXP - currentLevelXP;
  const progress = xpInCurrentLevel / xpNeededForNextLevel;

  return {
    currentLevel,
    currentLevelXP,
    nextLevelXP,
    xpInCurrentLevel,
    xpNeededForNextLevel,
    xpToNextLevel: nextLevelXP - total_xp,
    progress: Math.min(progress, 1), // Cap at 100%
    nextLevelPerks: getNextLevelPerks(total_xp),
  };
};

// ============================================================================
// COIN CALCULATIONS
// ============================================================================

/**
 * Calculate vault balance (approved coins in vault account)
 */
export const calculateVaultBalance = (coin_events: CoinEvent[]): number => {
  return coin_events
    .filter((e) => e.account_type === "vault" && e.status === "approved")
    .reduce((total, event) => total + event.amount, 0);
};

/**
 * Calculate balance for a specific savings account
 */
export const calculateSavingsBalance = (
  coin_events: CoinEvent[],
  account_type: string,
): number => {
  return coin_events
    .filter((e) => e.account_type === account_type && e.status === "approved")
    .reduce((total, event) => total + event.amount, 0);
};

/**
 * Calculate all savings balances
 */
export const calculateAllSavingsBalances = (coin_events: CoinEvent[]) => {
  return {
    savings_10: calculateSavingsBalance(coin_events, "savings_10"),
    savings_40: calculateSavingsBalance(coin_events, "savings_40"),
    savings_100: calculateSavingsBalance(coin_events, "savings_100"),
    savings_200: calculateSavingsBalance(coin_events, "savings_200"),
  };
};

/**
 * Calculate total wealth (vault + all savings)
 */
export const calculateTotalWealth = (coin_events: CoinEvent[]): number => {
  const vault = calculateVaultBalance(coin_events);
  const savings = calculateAllSavingsBalances(coin_events);
  const totalSavings = Object.values(savings).reduce(
    (sum, amount) => sum + amount,
    0,
  );
  return vault + totalSavings;
};

/**
 * Get pending coin requests (not yet approved/rejected)
 */
export const getPendingCoinRequests = (
  coin_events: CoinEvent[],
): CoinEvent[] => {
  return coin_events.filter((e) => e.status === "pending");
};

/**
 * Get bank account summaries with balances
 */
export const getBankAccountSummaries = (
  coin_events: CoinEvent[],
): BankAccountSummary[] => {
  return BANK_ACCOUNTS.map((account) => {
    const balance =
      account.type === "vault"
        ? calculateVaultBalance(coin_events)
        : calculateSavingsBalance(coin_events, account.type);

    const accountEvents = coin_events.filter(
      (e) => e.account_type === account.type && e.status === "approved",
    );
    const deposits = accountEvents
      .filter((e) => e.amount > 0)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    const withdrawals = accountEvents
      .filter((e) => e.amount < 0)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

    return {
      account_type: account.type,
      balance,
      interest_rate: account.rate,
      locked: account.locked,
      lock_period: account.lockPeriod,
      last_deposit: deposits[0]?.created_at,
      last_withdrawal: withdrawals[0]?.created_at,
    };
  });
};

// ============================================================================
// QUEST STATE CALCULATIONS
// ============================================================================

/**
 * Get the latest state for each quest
 * Groups by quest_id and takes most recent event
 */
export const getQuestStates = (
  quest_events: QuestEvent[],
): Record<string, QuestEvent> => {
  return quest_events.reduce(
    (states, event) => {
      const existing = states[event.quest_id];
      if (
        !existing ||
        new Date(event.created_at) > new Date(existing.created_at)
      ) {
        states[event.quest_id] = event;
      }
      return states;
    },
    {} as Record<string, QuestEvent>,
  );
};

/**
 * Get active quests (accepted or submitted, not completed/rejected/abandoned)
 */
export const getActiveQuests = (quest_events: QuestEvent[]): QuestEvent[] => {
  const latestStates = getQuestStates(quest_events);
  return Object.values(latestStates).filter(
    (event) =>
      event.event_type === "accepted" || event.event_type === "submitted",
  );
};

/**
 * Get completed quests
 */
export const getCompletedQuests = (
  quest_events: QuestEvent[],
): QuestEvent[] => {
  const latestStates = getQuestStates(quest_events);
  return Object.values(latestStates).filter(
    (event) => event.event_type === "approved",
  );
};

/**
 * Get quest state with full quest data
 */
export const getQuestStateWithData = (
  quest_events: QuestEvent[],
  quests: Quest[],
): QuestState[] => {
  const latestStates = getQuestStates(quest_events);

  return Object.entries(latestStates)
    .map(([quest_id, latest_event]) => {
      const quest = quests.find((q) => q.id === quest_id);
      if (!quest) return null;

      const allEvents = quest_events.filter((e) => e.quest_id === quest_id);
      const submittedEvent = allEvents.find(
        (e) => e.event_type === "submitted",
      );
      const approvedEvent = allEvents.find((e) => e.event_type === "approved");

      return {
        quest_id,
        quest,
        latest_event,
        status: latest_event.event_type as any,
        submitted_at: submittedEvent?.created_at,
        approved_at: approvedEvent?.created_at,
      };
    })
    .filter(Boolean) as QuestState[];
};

/**
 * Count quests by status
 */
export const countQuestsByStatus = (quest_events: QuestEvent[]) => {
  const latestStates = getQuestStates(quest_events);
  const statuses = Object.values(latestStates).map((e) => e.event_type);

  return {
    accepted: statuses.filter((s) => s === "accepted").length,
    submitted: statuses.filter((s) => s === "submitted").length,
    approved: statuses.filter((s) => s === "approved").length,
    rejected: statuses.filter((s) => s === "rejected").length,
    abandoned: statuses.filter((s) => s === "abandoned").length,
    total: statuses.length,
  };
};

// ============================================================================
// BADGE CALCULATIONS
// ============================================================================

/**
 * Get badge progress (highest tier earned per badge)
 */
export const getBadgeProgress = (
  badge_events: BadgeEvent[],
): Record<string, BadgeProgress> => {
  const tierOrder = ["bronze", "silver", "gold", "platinum", "legendary"];
  const getTierValue = (tier: string) => tierOrder.indexOf(tier);

  return badge_events.reduce(
    (progress, event) => {
      const existing = progress[event.badge_id];

      if (!existing) {
        progress[event.badge_id] = {
          badge_id: event.badge_id,
          highest_tier: event.tier,
          total_xp_earned: event.xp_awarded,
          earned_at: event.created_at,
        };
      } else {
        // Update if this is a higher tier
        if (getTierValue(event.tier) > getTierValue(existing.highest_tier!)) {
          progress[event.badge_id].highest_tier = event.tier;
          progress[event.badge_id].earned_at = event.created_at;
        }
        progress[event.badge_id].total_xp_earned += event.xp_awarded;
      }

      return progress;
    },
    {} as Record<string, BadgeProgress>,
  );
};

/**
 * Count total badges earned
 */
export const countBadgesEarned = (badge_events: BadgeEvent[]): number => {
  return Object.keys(getBadgeProgress(badge_events)).length;
};

/**
 * Get recently earned badges (last N)
 */
export const getRecentBadges = (
  badge_events: BadgeEvent[],
  limit: number = 5,
): BadgeEvent[] => {
  return [...badge_events]
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
    .slice(0, limit);
};

// ============================================================================
// COMPLETE STUDENT STATS
// ============================================================================

/**
 * Compute all student stats from a user object
 * This is the main function to use in dashboards
 */
export const computeStudentStats = (user: TISAUser): StudentStats => {
  const total_xp = calculateTotalXP(user.xp_events);
  const levelProgress = calculateLevelProgress(total_xp);
  const vault_balance = calculateVaultBalance(user.coin_events);
  const savings_balances = calculateAllSavingsBalances(user.coin_events);
  const total_savings = Object.values(savings_balances).reduce(
    (sum, amount) => sum + amount,
    0,
  );
  const questCounts = countQuestsByStatus(user.quest_events);

  return {
    total_xp,
    current_level: levelProgress.currentLevel,
    xp_to_next_level: levelProgress.xpToNextLevel,
    vault_balance,
    savings_balances,
    total_savings,
    total_wealth: vault_balance + total_savings,
    badges_earned: countBadgesEarned(user.badge_events),
    quests_completed: questCounts.approved,
    quests_active: questCounts.accepted + questCounts.submitted,
  };
};

// ============================================================================
// EVENT FILTERING & SORTING
// ============================================================================

/**
 * Get events within a date range
 */
export const filterEventsByDateRange = <T extends { created_at: string }>(
  events: T[],
  startDate: Date,
  endDate: Date,
): T[] => {
  return events.filter((event) => {
    const eventDate = new Date(event.created_at);
    return eventDate >= startDate && eventDate <= endDate;
  });
};

/**
 * Get today's events
 */
export const getTodayEvents = <T extends { created_at: string }>(
  events: T[],
): T[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return filterEventsByDateRange(events, today, tomorrow);
};

/**
 * Get this week's events
 */
export const getThisWeekEvents = <T extends { created_at: string }>(
  events: T[],
): T[] => {
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 7);
  return filterEventsByDateRange(events, startOfWeek, endOfWeek);
};

/**
 * Get this month's events
 */
export const getThisMonthEvents = <T extends { created_at: string }>(
  events: T[],
): T[] => {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0,
    23,
    59,
    59,
  );
  return filterEventsByDateRange(events, startOfMonth, endOfMonth);
};

/**
 * Sort events by date (newest first by default)
 */
export const sortEventsByDate = <
  T extends { created_at?: string; date?: string },
>(
  events: T[],
  order: "asc" | "desc" = "desc",
): T[] => {
  return [...events].sort((a, b) => {
    const dateA = new Date(a.created_at || a.date || "").getTime();
    const dateB = new Date(b.created_at || b.date || "").getTime();
    return order === "desc" ? dateB - dateA : dateA - dateB;
  });
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Check if user can afford a purchase
 */
export const canAfford = (user: TISAUser, amount: number): boolean => {
  const vaultBalance = calculateVaultBalance(user.coin_events);
  return vaultBalance >= amount;
};

/**
 * Check if user has reached a specific level
 */
export const hasReachedLevel = (
  user: TISAUser,
  requiredLevel: number,
): boolean => {
  const totalXP = calculateTotalXP(user.xp_events);
  const currentLevel = XP_TO_LEVEL(totalXP);
  return currentLevel >= requiredLevel;
};

/**
 * Check if user has earned a specific badge
 */
export const hasBadge = (
  user: TISAUser,
  badgeId: string,
  minTier?: string,
): boolean => {
  const progress = getBadgeProgress(user.badge_events);
  const badgeProgress = progress[badgeId];

  if (!badgeProgress) return false;
  if (!minTier) return true;

  const tierOrder = ["bronze", "silver", "gold", "platinum", "legendary"];
  const currentTierIndex = tierOrder.indexOf(badgeProgress.highest_tier!);
  const requiredTierIndex = tierOrder.indexOf(minTier);

  return currentTierIndex >= requiredTierIndex;
};

// ============================================================================
// LEGEND CLUB HELPERS
// ============================================================================

/**
 * Build complete timeline from all user events
 * Combines badges, quests, level-ups, and projects into chronological order
 */
export const buildTimeline = (
  user: TISAUser,
): import("../types/tisa-coins").TimelineItem[] => {
  const items: import("../types/tisa-coins").TimelineItem[] = [];

  // Add badge events
  user.badge_events?.forEach((event) => {
    const badge = getBadgeById(event.badge_id);
    if (!badge) return;

    items.push({
      id: `badge-${event.badge_id}-${event.created_at}`,
      type: "badge",
      title: `Earned ${badge.name}`,
      date: event.created_at,
      icon: badge.icon,
      description: `${event.tier.toUpperCase()} tier - ${event.note}`,
      metadata: {
        tier: event.tier,
        xp: event.xp_awarded,
        badge_id: event.badge_id,
      },
    });
  });

  // Add quest completions
  const completedQuests = getCompletedQuests(user.quest_events || []);
  completedQuests.forEach((event) => {
    items.push({
      id: `quest-${event.quest_id}-${event.created_at}`,
      type: "quest",
      title: `Completed Quest`,
      date: event.created_at,
      icon: "🎯",
      description: event.note || "Quest completed!",
      metadata: {
        quest_id: event.quest_id,
      },
    });
  });

  // Detect level-ups from XP history
  if (user.xp_events && user.xp_events.length > 0) {
    let runningXP = 0;
    let currentLevel = 1;

    user.xp_events.forEach((event) => {
      runningXP += event.amount;
      const newLevel = XP_TO_LEVEL(runningXP);

      if (newLevel > currentLevel) {
        items.push({
          id: `levelup-${newLevel}-${event.created_at}`,
          type: "level_up",
          title: `Reached Level ${newLevel}`,
          date: event.created_at,
          icon: "⭐",
          description: `Level up! Now at Level ${newLevel}`,
          metadata: {
            level: newLevel,
            total_xp: runningXP,
          },
        });
        currentLevel = newLevel;
      }
    });
  }

  // Add projects from legend_data
  if (user.legend_data?.showcase_projects) {
    user.legend_data.showcase_projects.forEach((project) => {
      if (project.showcase) {
        items.push({
          id: `project-${project.id}`,
          type: "project",
          title: project.title,
          date: project.created_at,
          icon: getCategoryIcon(project.category),
          description: project.description,
          metadata: {
            category: project.category,
            media_url: project.media_url,
          },
        });
      }
    });
  }

  // Sort by date (newest first)
  return sortEventsByDate(items, "desc");
};

/**
 * Get showcase badges for profile display
 */
export const getShowcaseBadges = (
  user: TISAUser,
): Array<{
  badge: import("../config/tisa-settings").Badge;
  highestTier: string;
}> => {
  const showcaseBadgeIds = user.legend_data?.showcase_badges || [];
  const badgeProgress = getBadgeProgress(user.badge_events || []);

  return showcaseBadgeIds
    .map((badgeId) => {
      const badge = getBadgeById(badgeId);
      const progress = badgeProgress[badgeId];
      if (!badge || !progress || !progress.highest_tier) return null;

      return {
        badge,
        highestTier: progress.highest_tier as string,
      };
    })
    .filter(
      (
        item,
      ): item is {
        badge: import("../config/tisa-settings").Badge;
        highestTier: string;
      } => item !== null,
    );
};

/**
 * Compute automatic achievements based on user stats
 */
export const getUnlockedAchievements = (
  user: TISAUser,
): import("../types/tisa-coins").Achievement[] => {
  const stats = computeStudentStats(user);
  const achievements: import("../types/tisa-coins").Achievement[] = [];

  // First Quest
  if (stats.quests_completed >= 1) {
    achievements.push({
      id: "first-quest",
      title: "Quest Pioneer",
      description: "Completed your first quest",
      icon: "🎯",
      category: "academic",
      unlocked_at:
        getCompletedQuests(user.quest_events || [])[0]?.created_at ||
        new Date().toISOString(),
    });
  }

  // Badge Collector
  if (stats.badges_earned >= 5) {
    achievements.push({
      id: "badge-collector",
      title: "Badge Collector",
      description: "Earned 5 or more badges",
      icon: "🏆",
      category: "academic",
      unlocked_at: user.badge_events[4]?.created_at || new Date().toISOString(),
    });
  }

  // Level Master
  if (stats.current_level >= 10) {
    achievements.push({
      id: "level-master",
      title: "Level Master",
      description: "Reached Level 10",
      icon: "⭐",
      category: "academic",
      unlocked_at:
        user.xp_events[user.xp_events.length - 1]?.created_at ||
        new Date().toISOString(),
    });
  }

  // Coin Saver
  if (stats.total_savings >= 1000) {
    achievements.push({
      id: "coin-saver",
      title: "Coin Saver",
      description: "Saved 1000+ coins in bank",
      icon: "💰",
      category: "leadership",
      unlocked_at: new Date().toISOString(),
    });
  }

  // Quest Champion (10 quests)
  if (stats.quests_completed >= 10) {
    achievements.push({
      id: "quest-champion",
      title: "Quest Champion",
      description: "Completed 10+ quests",
      icon: "🏅",
      category: "academic",
      unlocked_at: new Date().toISOString(),
    });
  }

  return achievements;
};

/**
 * Get aggregate stats for Legend profile header
 */
export const getLegendStats = (user: TISAUser) => {
  const stats = computeStudentStats(user);
  const timeline = buildTimeline(user);
  const achievements = getUnlockedAchievements(user);

  return {
    totalXP: stats.total_xp,
    currentLevel: stats.current_level,
    badgesEarned: stats.badges_earned,
    questsCompleted: stats.quests_completed,
    totalWealth: stats.total_wealth,
    timelineEvents: timeline.length,
    achievementsUnlocked: achievements.length,
    profileLikes: user.legend_data?.social?.received_likes?.length || 0,
    endorsements: user.legend_data?.social?.endorsements?.length || 0,
  };
};

/**
 * Get category icon for projects
 */
const getCategoryIcon = (category: string): string => {
  const icons: Record<string, string> = {
    coding: "💻",
    art: "🎨",
    writing: "✍️",
    science: "🔬",
    other: "📁",
  };
  return icons[category] || icons.other;
};

/**
 * Initialize default legend_data structure
 */
export const initializeLegendData =
  (): import("../types/tisa-coins").LegendData => {
    return {
      bio: "",
      tagline: "",
      achievements: [],
      showcase_badges: [],
      showcase_projects: [],
      timeline_items: [],
      profile_customization: {
        theme_color: "purple",
        visible_sections: ["timeline", "badges", "stats", "projects", "bio"],
      },
      social: {
        received_likes: [],
        endorsements: [],
      },
      stats_cache: {
        profile_visits: 0,
        last_updated: null,
      },
    };
  };

// ============================================================================
// EXPORT ALL HELPERS
// ============================================================================

export default {
  // XP & Level
  calculateTotalXP,
  calculateLevelProgress,

  // Coins & Bank
  calculateVaultBalance,
  calculateSavingsBalance,
  calculateAllSavingsBalances,
  calculateTotalWealth,
  getPendingCoinRequests,
  getBankAccountSummaries,

  // Quests
  getQuestStates,
  getActiveQuests,
  getCompletedQuests,
  getQuestStateWithData,
  countQuestsByStatus,

  // Badges
  getBadgeProgress,
  countBadgesEarned,
  getRecentBadges,

  // Complete Stats
  computeStudentStats,

  // Filtering & Sorting
  filterEventsByDateRange,
  getTodayEvents,
  getThisWeekEvents,
  getThisMonthEvents,
  sortEventsByDate,

  // Validation
  canAfford,
  hasReachedLevel,
  hasBadge,

  // Legend Club
  buildTimeline,
  getShowcaseBadges,
  getUnlockedAchievements,
  getLegendStats,
  initializeLegendData,
};
